import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, Form, Button, Row, FormControl } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useFranchiseFineStore } from '../../store/providers/FranchiseFine';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import InputCollection from '../../components/InputCollection/InputCollection';
import withForm from '../../hoc/withForm';
import OptionsHelper from '../../lib/OptionsHelper';

import FilterGroups from './FormSections/FilterGroups';
import Escalations from './FormSections/Escalations';
import AssignInstances from './FormSections/AssignInstances';
import Recipients from './FormSections/Recipients';

import validations from './validations';
import Permission from '../../components/Permission/Permission';

import './style.css';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function Fine(props) {
    const dispatch = useDispatch();

    const pagePermission = constants.PERMISSION_SETTINGS_FINES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const { selectRecord, createRecord, updateRecord, deleteRecord, listLookupValues, clearData, data: franchiseFineData } = useFranchiseFineStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [selectedFranchiseOptions, setSelectedFranchiseOptions] = useState([]);
    const [reasonTypeLookups, setReasonTypeLookups] = useState([]);
    const [ruleTypes, setRuleTypes] = useState([]);
    const [feeRecipientTypes, setFeeRecipientTypes] = useState([]);
    const [franchiseOptions, setFranchiseOptions] = useState([]);
    const [selectedRuleTypeId, setSelectedRuleTypeId] = useState(null);
    const [filterGroups, setFilterGroups] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [escalationTypes, setEscalationTypes] = useState([]);
    const [selectedEscalationTypeId, setSelectedEscalationTypeId] = useState('');
    const [escalations, setEscalations] = useState([]); // [{ id, from, to, amount }]

    const selectedReasonTypeId = props.formData?.reasonTypeId;
    const reasonTypes = useMemo(() => {
        const parsedRuleTypeId = parseInt(selectedRuleTypeId || 0, 10);
        const filtered = reasonTypeLookups
            .filter((reasonType) => parseInt(reasonType.ruleTypeId || 0, 10) === parsedRuleTypeId)
            .map((reasonType) => ({ id: reasonType.id, name: reasonType.name }));
        return OptionsHelper({ list: filtered, nameField: 'name', includeBlank: true, includeBlankName: 'Select...' });
    }, [reasonTypeLookups, selectedRuleTypeId]);

    // Reset mounted state when fineId changes to allow reinitialization
    useEffect(() => {
        setIsMounted(false);
    }, [props.fineId]);

    const createNewFine = useCallback(() => {
        return {
            id: null,
            name: '',
            reasonTypeId: '',
            reasonTypeName: '',
            ruleTypeId: '',
            ruleTypeName: '',
            gracePeriodDays: 0,
            feeRecipientTypeId: '',
            feeRecipientTypeName: '',
            excludeTeamLead: false,
            excludeMentor: false,
            excludeTransactionCoordinator: false,
            noteTemplate: '',
            filterGroups: [],
            franchiseIds: [],
            escalationTypeId: constants.EscalationTypeEnum.Never,
            escalations: []
        };
    }, []);

    const handleRuleTypeChange = (ruleTypeId) => {
        const parsedRuleTypeId = parseInt(ruleTypeId || 0, 10);
        setSelectedRuleTypeId(!isNaN(parsedRuleTypeId) && parsedRuleTypeId > 0 ? parsedRuleTypeId : null);
        setFilterGroups([]);
        props.formChanged('filterGroups', []);
        props.formChanged('reasonTypeId', '');
    };

    const handleFilterGroupsChanged = (apiGroups) => {
        setFilterGroups(apiGroups);
        updateFormFilterGroups(apiGroups);
    };

    const updateFormFilterGroups = (groups) => {
        const apiGroups = groups.map(group => ({
            id: group.id,
            filterKey: group.filterKey,
            values: (group.values || []).map(v => ({ name: v.name, value: v.value }))
        }));
        props.formChanged('filterGroups', apiGroups);
    };

    const onAssignInstancesChange = (list) => {
        setSelectedFranchiseOptions(list);
        props.formChanged('franchises', list);
    };

    const formCancelled = () => {
        navigate(`/fines`);
    };

    const showDeleteConfirmation = () => {
        setShowDeleteModal(true);
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
    };

    const confirmDelete = () => {
        setShowDeleteModal(false);
        if (props.formData.id) {
            deleteRecord({ fineId: props.formData.id });
        }
    };
    const submittedTemplateHandler = (e) => {
        e.preventDefault();

        let franchiseIds = [];
        franchiseIds = selectedFranchiseOptions.map((selectedFranchise) => {
            return selectedFranchise.id;
        })

        props.formData.franchises = franchiseIds;

        var valid = props.formValidate(validations);
        if (valid) {
            props.formSubmitted();

            // Build escalations payload based on escalation type selection
            const escalationTypeId = parseInt(props.formData.escalationTypeId || selectedEscalationTypeId || 0);
            let escalationPayload = [];
            if (escalationTypeId === constants.EscalationTypeEnum.Never) {
                // Single amount, system will use From=1, To=1
                const amount = (props.formData.escalations && props.formData.escalations[0]?.amount) ?? (escalations[0]?.amount);
                if (!_.isNil(amount) && amount !== '') {
                    escalationPayload = [{ id: props.formData.escalations && props.formData.escalations[0]?.id ? props.formData.escalations[0].id : null, from: 1, to: 1, amount: parseFloat(amount) }];
                }
            } else if (escalationTypeId === constants.EscalationTypeEnum.Monthly || escalationTypeId === constants.EscalationTypeEnum.Yearly) {
                const sourceRows = props.formData.escalations?.length ? props.formData.escalations : escalations;
                escalationPayload = (sourceRows || [])
                    .filter(row => row)
                    .map(row => ({
                        id: _.isNumber(row.id) ? row.id : (row.id || null),
                        from: parseInt(row.from || row.From || 0),
                        to: parseInt(row.to || row.To || 0),
                        amount: parseFloat(row.amount || row.Amount || 0)
                    }))
                    .filter(row => !isNaN(row.from) && !isNaN(row.to) && !isNaN(row.amount));
            }

            let submitObject = {
                id: props.formData.id,
                name: props.formData.name,
                reasonTypeId: props.formData.reasonTypeId,
                ruleTypeId: props.formData.ruleTypeId,
                gracePeriodDays: props.formData.gracePeriodDays || 0,
                feeRecipientTypeId: props.formData.feeRecipientTypeId,
                noteTemplate: props.formData.noteTemplate || '',
                filterGroups: props.formData.filterGroups || [],
                franchiseIds: franchiseIds,
                escalationTypeId: escalationTypeId || null,
                escalations: escalationPayload,
                excludeTeamLead: props.formData.excludeTeamLead || false,
                excludeMentor: props.formData.excludeMentor || false,
                excludeTransactionCoordinator: props.formData.excludeTransactionCoordinator || false
            };

            isNew ? createRecord(submitObject) : updateRecord(submitObject);
        }
    };


    useEffect(() => {
        if (!_.isEmpty(franchiseFineData)) {
            if ((franchiseFineData.scope === 'create'
                || franchiseFineData.scope === 'update'
                || franchiseFineData.scope === 'edit'
                || franchiseFineData.scope === 'delete')
                && franchiseFineData.status === 'success') {
                navigate(`/fines`);
            }

            if (!isLoaded && isMounted) {
                if (franchiseFineData.scope === 'select' && franchiseFineData.status === 'success') {
                    const fineData = franchiseFineData.body;

                    let franchiseArray = [];
                    if (fineData.franchises) {
                        franchiseArray = fineData.franchises.map(franchise => ({
                            id: franchise.id,
                            name: franchise.name
                        }));
                    }
                    setSelectedFranchiseOptions(franchiseArray);

                    if (fineData.ruleTypeId) {
                        setSelectedRuleTypeId(parseInt(fineData.ruleTypeId));
                    }

                    if (fineData.escalationTypeId) {
                        setSelectedEscalationTypeId(fineData.escalationTypeId);
                        props.formChanged('escalationTypeId', fineData.escalationTypeId);
                    }
                    if (fineData.escalations && Array.isArray(fineData.escalations)) {
                        const mapped = fineData.escalations.map(e => ({
                            id: e.id ?? null,
                            from: e.from ?? e.From,
                            to: e.to ?? e.To,
                            amount: e.amount ?? e.Amount
                        }));
                        setEscalations(mapped);
                        props.formChanged('escalations', mapped);
                    }

                    if (fineData.filterGroups && Array.isArray(fineData.filterGroups)) {
                        setFilterGroups(fineData.filterGroups.map(fg => ({
                            id: fg.id,
                            filterKey: fg.filterKey,
                            values: (fg.values || []).map(v => ({ name: v.name, value: v.value }))
                        })));
                    }

                    dispatch(breadcrumbAddRoute({
                        key: 'edit-fine',
                        text: fineData.name,
                        path: {
                            event: () => {
                                navigate(`/fines/edit/${fineData.id}`);
                            }
                        }
                    }));

                    props.formInitialize(fineData);
                    setIsLoaded(true);
                }
                setIsBusy(
                    franchiseFineData.status === 'processing'
                    && (franchiseFineData.scope === 'edit' || franchiseFineData.scope === 'update' || franchiseFineData.scope === 'create' || franchiseFineData.scope === 'delete')
                );
            }
        }

    }, [franchiseFineData,
        props,
        isNew,
        isLoaded,
        isMounted,
        clearData,
        dispatch,
        reasonTypeLookups,
        ruleTypes,
        feeRecipientTypes,
        escalationTypes,
        franchiseOptions
    ]);

    // Init (request lookups + select record if editing) - only run once per fineId
    useEffect(() => {
        if (!hasPermission) return;
        if (isMounted) return; // Prevent multiple runs

        // Reset all lookup state when component mounts to ensure fresh state
        setReasonTypeLookups([]);
        setRuleTypes([]);
        setFeeRecipientTypes([]);
        setEscalationTypes([]);
        setFranchiseOptions([]);
        setLookupsLoaded(false);
        setIsLoaded(false);

        listLookupValues(); // fire (cached or network)
        if (!_.isEmpty(props.fineId)) {
            selectRecord({ fineId: props.fineId });
            setIsNew(false);
        } else {
            setIsNew(true);
        }

        dispatch(breadcrumbAddRoute({
            key: 'fines', text: 'Fines', path: {
                event: () => {
                    dispatch(breadcrumbRemoveRoute(2));
                    navigate('/fines');
                }
            }
        }));
        setIsMounted(true);
    }, [hasPermission, props.fineId, isMounted, listLookupValues, selectRecord, dispatch]);

    // Process lookup values
    useEffect(() => {
        if (!franchiseFineData) return;
        if (franchiseFineData.scope !== 'list_lookupvalues' || franchiseFineData.status !== 'success') return;

        const body = franchiseFineData.body || {};

        // Always populate lookup values when we receive them, regardless of current state
        // This ensures cached data gets loaded properly on navigation
        if (body.reasonTypes) {
            const reasonTypesList = body.reasonTypes.map((r) => ({
                id: r.id,
                name: r.name,
                ruleTypeId: r.ruleTypeId ?? r.RuleTypeId
            }));
            setReasonTypeLookups(reasonTypesList);
        }
        if (body.ruleTypes) {
            const ruleTypesList = body.ruleTypes.map(r => ({ id: r.id, name: r.name }));
            setRuleTypes(OptionsHelper({ list: ruleTypesList, nameField: 'name', includeBlank: true, includeBlankName: 'Select...' }));
        }
        if (body.feeRecipientTypes) {
            const feeRecipientTypesList = body.feeRecipientTypes.map(r => ({ id: r.id, name: r.name }));
            setFeeRecipientTypes(OptionsHelper({ list: feeRecipientTypesList, nameField: 'name', includeBlank: true, includeBlankName: 'Select...' }));
        }
        if (body.escalationTypes) {
            const escList = body.escalationTypes.map(e => ({ id: e.id, name: e.name }));
            setEscalationTypes(OptionsHelper({ list: escList, nameField: 'name', includeBlank: true, includeBlankName: 'Select...' }));
        }
        if (body.franchises) {
            const franchiseList = body.franchises.map(f => ({ id: f.id, name: f.name, corporateOwned: f.isCorporate }));
            setFranchiseOptions(franchiseList);
        }

        // If all core lookups present, mark loaded
        if (!lookupsLoaded && body.reasonTypes && body.ruleTypes && body.feeRecipientTypes && body.franchises) {
            setLookupsLoaded(true);
        }
    }, [franchiseFineData, lookupsLoaded]);

    useEffect(() => {
        const parsedReasonTypeId = parseInt(selectedReasonTypeId || 0, 10);
        if (!parsedReasonTypeId) return;

        const parsedRuleTypeId = parseInt(selectedRuleTypeId || 0, 10);
        if (!parsedRuleTypeId) {
            props.formChanged('reasonTypeId', '');
            return;
        }

        const isValidForRuleType = reasonTypeLookups.some((reasonType) => (
            parseInt(reasonType.id || 0, 10) === parsedReasonTypeId
            && parseInt(reasonType.ruleTypeId || 0, 10) === parsedRuleTypeId
        ));

        if (!isValidForRuleType) {
            props.formChanged('reasonTypeId', '');
        }
    }, [selectedReasonTypeId, selectedRuleTypeId, reasonTypeLookups, props.formChanged]);

    // Process fine selection (edit mode)
    useEffect(() => {
        if (!franchiseFineData) return;
        if (isLoaded) return;
        if (!isMounted) return;
        if (franchiseFineData.scope !== 'select' || franchiseFineData.status !== 'success') return;

        const fineData = franchiseFineData.body;
        if (!fineData) return;

        const franchiseArray = (fineData.franchises || []).map(fr => ({ id: fr.id, name: fr.name }));
        setSelectedFranchiseOptions(franchiseArray);

        if (fineData.ruleTypeId) setSelectedRuleTypeId(parseInt(fineData.ruleTypeId));

        if (fineData.escalationTypeId) {
            setSelectedEscalationTypeId(fineData.escalationTypeId);
            props.formChanged('escalationTypeId', fineData.escalationTypeId);
        }
        if (Array.isArray(fineData.escalations)) {
            const mapped = fineData.escalations.map(e => ({ id: e.id ?? null, from: e.from ?? e.From, to: e.to ?? e.To, amount: e.amount ?? e.Amount }));
            setEscalations(mapped);
            props.formChanged('escalations', mapped);
        }
        if (Array.isArray(fineData.filterGroups)) {
            setFilterGroups(fineData.filterGroups.map(fg => ({ id: fg.id, filterKey: fg.filterKey, values: (fg.values || []).map(v => ({ name: v.name, value: v.value })) })));
        }

        dispatch(breadcrumbAddRoute({
            key: 'edit-fine',
            text: fineData.name,
            path: { event: () => navigate(`/fines/edit/${fineData.id}`) }
        }));

        props.formInitialize(fineData);
        setIsLoaded(true);
    }, [franchiseFineData, isLoaded, isMounted, dispatch, props]);

    // Track busy state for create/update/delete flows
    useEffect(() => {
        if (!franchiseFineData) return;
        const busy = franchiseFineData.status === 'processing' && ['edit', 'update', 'create', 'delete'].includes(franchiseFineData.scope);
        setIsBusy(busy);
        if (['create', 'update', 'edit', 'delete'].includes(franchiseFineData.scope) && franchiseFineData.status === 'success') {
            navigate('/fines');
        }
    }, [franchiseFineData]);

    // New fine initialization once lookups are ready
    useEffect(() => {
        if (lookupsLoaded && isNew && !isLoaded) {
            const newFine = createNewFine();
            dispatch(breadcrumbAddRoute({
                key: 'new-fine',
                text: 'new',
                path: { event: () => navigate('/fines/new') }
            }));
            props.formInitialize(newFine);
            setIsLoaded(true);
        }
    }, [lookupsLoaded, isNew, isLoaded, createNewFine, dispatch, props]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <Suspense fallback={<Spinner text="Loading Fine..." />}>
            {
                <>
                    <SideMenu activeKey="fines" />
                    <div className="content-body">
                        <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />

                        {hasPermission && isMounted &&
                            <>
                                {!isLoaded &&
                                    <Spinner text="Loading Fine..." />
                                }
                                {isLoaded &&
                                    <div style={{ paddingRight: '15px' }}>
                                        {!isNew &&
                                            <PageHeader title={"Edit Fine: " + props.formData['name']} />
                                        }
                                        {isNew &&
                                            <PageHeader title={"New Fine"} />
                                        }
                                        <Panel>
                                            <Panel.Body>
                                                <Form>
                                                    <Row>
                                                        <div className="col-md-12">
                                                            <InputCollection required labelText="Name" errorMessage={props.formValidationCheck('name')}>
                                                                <FormControl
                                                                    id="name"
                                                                    name="name"
                                                                    onChange={(e) => {
                                                                        props.formChanged(e.target.id, e.target.value);
                                                                    }}
                                                                    defaultValue={props.formData['name'] || ''}
                                                                    disabled={isBusy}
                                                                />
                                                            </InputCollection>
                                                        </div>
                                                    </Row>

                                                    <Row>
                                                        {/* Rule Type */}
                                                        <div className="col-md-3">
                                                            <InputCollection required labelText="Rule Type" errorMessage={props.formValidationCheck('ruleTypeId')} >
                                                                {!_.isEmpty(ruleTypes) && (
                                                                    <FormControl
                                                                        id="ruleTypeId"
                                                                        componentClass="select"
                                                                        value={props.formData['ruleTypeId'] || ''}
                                                                        onChange={(e) => {
                                                                            if (isNew) {
                                                                                props.formChanged(e.target.id, e.target.value);
                                                                                handleRuleTypeChange(e.target.value);
                                                                            }
                                                                        }}
                                                                        disabled={isBusy || !isNew}
                                                                    >
                                                                        {ruleTypes}
                                                                    </FormControl>
                                                                )}
                                                            </InputCollection>
                                                        </div>
                                                        {/* Reason Type */}
                                                        <div className="col-md-3">
                                                            <InputCollection labelText="Reason Type" errorMessage={props.formValidationCheck('reasonTypeId')} >
                                                                <FormControl
                                                                    id="reasonTypeId"
                                                                    componentClass="select"
                                                                    value={props.formData['reasonTypeId'] || ''}
                                                                    onChange={(e) => {
                                                                        if (isNew) {
                                                                            props.formChanged(e.target.id, e.target.value);
                                                                        }
                                                                    }}
                                                                    disabled={isBusy || !isNew || !selectedRuleTypeId}
                                                                >
                                                                    {reasonTypes}
                                                                </FormControl>
                                                            </InputCollection>
                                                        </div>
                                                        {/* Grace Period Days */}
                                                        <div className="col-md-3">
                                                            <InputCollection labelText="Grace Period Days" errorMessage={props.formValidationCheck('gracePeriodDays')}>
                                                                <FormControl
                                                                    id="gracePeriodDays"
                                                                    name="gracePeriodDays"
                                                                    type="number"
                                                                    onChange={(e) => {
                                                                        props.formChanged(e.target.id, e.target.value);
                                                                    }}
                                                                    defaultValue={props.formData['gracePeriodDays'] || 0}
                                                                    disabled={isBusy}
                                                                />
                                                            </InputCollection>
                                                        </div>
                                                    </Row>

                                                    <Row>
                                                        <div className="col-md-12">
                                                            <InputCollection labelText="Note Template" errorMessage={props.formValidationCheck('noteTemplate')}>
                                                                <FormControl
                                                                    id="noteTemplate"
                                                                    name="noteTemplate"
                                                                    componentClass="textarea"
                                                                    rows={3}
                                                                    onChange={(e) => {
                                                                        props.formChanged(e.target.id, e.target.value);
                                                                    }}
                                                                    defaultValue={props.formData['noteTemplate'] || ''}
                                                                    disabled={isBusy}
                                                                    placeholder="Enter note template for fine notifications..."
                                                                />
                                                            </InputCollection>
                                                        </div>
                                                    </Row>

                                                    <Recipients
                                                        feeRecipientTypes={feeRecipientTypes}
                                                        formData={props.formData}
                                                        formChanged={props.formChanged}
                                                        formValidationCheck={props.formValidationCheck}
                                                        isBusy={isBusy}
                                                    />

                                                    <Escalations
                                                        escalationTypesOptions={escalationTypes}
                                                        selectedEscalationTypeId={props.formData['escalationTypeId'] || selectedEscalationTypeId}
                                                        onTypeChange={(val) => {
                                                            setSelectedEscalationTypeId(val);
                                                            props.formChanged('escalationTypeId', val);
                                                            setEscalations([]);
                                                            props.formChanged('escalations', []);
                                                        }}
                                                        rows={escalations}
                                                        onRowsChange={(next) => {
                                                            setEscalations(next);
                                                            props.formChanged('escalations', next);
                                                        }}
                                                        isBusy={isBusy}
                                                        formValidationCheck={props.formValidationCheck}
                                                        fallbackEscalationTypeName={props.formData.escalationTypeName}
                                                    />

                                                    {selectedRuleTypeId && (
                                                        <FilterGroups
                                                            selectedRuleTypeId={selectedRuleTypeId}
                                                            initialGroups={filterGroups}
                                                            isBusy={isBusy}
                                                            onChange={handleFilterGroupsChanged}
                                                            errorMessage={props.formValidationCheck('filterGroups')}
                                                        />
                                                    )}

                                                    <AssignInstances
                                                        franchiseOptions={franchiseOptions}
                                                        selectedValues={selectedFranchiseOptions}
                                                        onSelectionChange={onAssignInstancesChange}
                                                        isBusy={isBusy}
                                                        errorMessage={props.formValidationCheck('franchises')}
                                                    />

                                                    <div className="form-action-buttons">
                                                        {!isNew && (
                                                            <div className="form-action-left">
                                                                <Button
                                                                    bsStyle="warning"
                                                                    onClick={() => showDeleteConfirmation()}
                                                                    className="button-group-margin"
                                                                    disabled={!canEdit || isBusy}
                                                                >
                                                                    <i className="fa fa-trash button-icon"></i>
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <Button
                                                                bsStyle="default"
                                                                onClick={() => formCancelled()}
                                                                className="button-group-margin"
                                                                disabled={isBusy}
                                                            >
                                                                <i className="fa fa-undo button-icon"></i>
                                                                Back
                                                            </Button>
                                                            <Button
                                                                bsStyle="danger"
                                                                variant="danger"
                                                                onClick={(e) => submittedTemplateHandler(e)}
                                                                type="submit"
                                                                className="button-group-margin"
                                                                disabled={!canEdit || isBusy}
                                                            >
                                                                <i className="fa fa-save button-icon"></i>
                                                                {isBusy ? 'Saving...' : 'Save & Exit'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Form>
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                }
                            </>
                        }
                    </div>

                    <ConfirmationModal
                        show={showDeleteModal}
                        title="Delete Fine Rule"
                        yesText="Delete"
                        noText="Cancel"
                        yesClicked={confirmDelete}
                        noClicked={cancelDelete}
                    >
                        <p>Are you sure you want to delete the fine rule "{props.formData?.name}"?</p>
                        <p><strong>This action cannot be undone.</strong></p>
                    </ConfirmationModal>
                </>
            }
        </Suspense>
    );
}

export default withAuthentication(withLayout(withForm(Fine)));